import React, { useState } from "react";
import {
  Button,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import styles from "../../Meta.module.css";
import helpIcon from "../../../../../../../assets/images/helpIcon.svg";
import ConnectStream from "./subComponents/ConnectStream";
import ConnectFields from "./subComponents/ConnectFields";
import { useDispatch } from "react-redux";
import {
  deleteStream,
  getStreams,
  resetMetaStatuses,
} from "../../../../service/IntegrationsSlice";
import {
  findBranchById,
  getTimeAgo,
} from "../../../../../../../components/CommonFunctions/CommonFunctions";
import { useSelector } from "react-redux";
import reloadIcon from "../../../../../../../assets/images/reloadIcon.svg";
import TableSkeleton from "../../../../../../../components/Skeletons/TableSkeleton";
import { getSubDomainAccessStatus } from "../../../../../../../components/RolePermissionAccess/RolePermissionAccess";
import DeletePopup from "../../../../../../../components/CommonPopups/DeletePopup";
import AddIcon from "@mui/icons-material/Add";

const StyledTableCellHead = styled(TableCell)(({ theme }) => ({
  fontFamily: "Poppins",
  fontWeight: "400",
  lineHeight: "18px",
  fontSize: "14px",
  color: "#FFFFFF",
  textTransform: "capitalize",
  padding: theme.spacing(1),
}));

const StyledTableCellBody = styled(TableCell)(({ theme }) => ({
  fontFamily: "Poppins",
  fontWeight: "400",
  fontSize: "13px",
  color: "#142C44",
  padding: theme.spacing(1),
  paddingTop: "15px",
  paddingBottom: "10px",
}));

function StreamsTab(props: any) {
  const {
    connectStage,
    setConnectStage,
    metaStreams,
    setValue,
    streamsLoading,
    handleTabSelect,
    handleTabChange,
    createStream,
    streamType,
  } = props;
  const dispatch = useDispatch();
  const activeInstitute = JSON.parse(localStorage.getItem("ACTIVE_INSTITUTE")!);
  const { userById } = useSelector((state: any) => state.userDetails);

  const hasAllBranchAccess = getSubDomainAccessStatus(
    "GLOBAL_PERMISSIONS",
    "BRANCH_ACCESS",
    "ALL_BRANCHES",
    userById?.rolePermission
  );
  const { deleteStreamSuccess, deleteStreamLoading } = useSelector(
    (state: any) => state.appIntegrations
  );
  const { branchList } = useSelector((state: any) => state.branch);

  const [streamName, setStreamName] = useState<any>("");
  const [validStateName, setValidStateName] = React.useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [deletePopup, setDeletePopup] = React.useState<boolean>(false);
  const [deleteItem, setDeleteItem] = useState<any>(null);

  const filteredMetaStreams =
    metaStreams?.filter((item: any) => {
      const searchTermLower = searchTerm?.toLowerCase() || "";
      const fullName = item?.streamName || "";
      const id = item?.metaAdId || "";

      const nameMatch = fullName?.toLowerCase().includes(searchTermLower);
      const idMatch = id?.toLowerCase().includes(searchTermLower);

      return nameMatch || idMatch;
    }) || [];

  const handleStreamName = React.useCallback((event: any) => {
    const value = event.target.value;
    const alphanumericValue = value.replace(/[^a-zA-Z0-9]/g, "");

    setStreamName(alphanumericValue);
    setValidStateName(!!alphanumericValue);
  }, []);

  const streamEdit = (item: any) => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    urlSearchParams.set("streamId", item?.id);
    urlSearchParams.delete("streamSearchTerm");
    const newUrl = `${window.location.pathname}?${urlSearchParams.toString()}`;
    window.history.pushState({}, "", newUrl);
    setConnectStage(1);
  };
  const ViewLeads = (item: any) => {
    handleTabChange(1);
    handleTabSelect("responses", item.id);

    const urlSearchParams = new URLSearchParams(window.location.search);
    urlSearchParams.delete("streamSearchTerm");
    const newUrl = `${window.location.pathname}?${urlSearchParams.toString()}`;
    window.history.pushState({}, "", newUrl);
  };

  const handleSearch = (event: any) => {
    setSearchTerm(event.target.value);
    const queryParams = new URLSearchParams(window.location.search);
    if (event.target.value.trim() === "") {
      queryParams.delete("streamSearchTerm");
    } else {
      queryParams.set("streamSearchTerm", event.target.value);
    }

    const newUrl = `${window.location.pathname}?${queryParams.toString()}`;
    window.history.pushState({}, "", newUrl);
  };

  const handleDeleteStream = (item: any) => {
    setDeleteItem(item);
    setDeletePopup(true);
  };

  const handleDelete = async (event: any) => {
    event.preventDefault();

    try {
      dispatch<any>(deleteStream(deleteItem?.id));
    } catch (error) {}
  };

  React.useEffect(() => {
    if (activeInstitute?.id)
      dispatch<any>(
        getStreams({
          id: activeInstitute?.id,
          status: "",
          branchId: hasAllBranchAccess ? "" : userById?.branchId,
        })
      );
    const urlSearchParams = new URLSearchParams(window.location.search);
    const streamSearchTerm = urlSearchParams.get("streamSearchTerm");

    if (streamSearchTerm) {
      setSearchTerm(streamSearchTerm);
    }
  }, []);

  React.useEffect(() => {
    if (deleteStreamSuccess !== true && deleteStreamSuccess !== false)
      dispatch<any>(
        getStreams({
          id: activeInstitute?.id,
          status: "",
          branchId: hasAllBranchAccess ? "" : userById?.branchId,
        })
      );
    setDeleteItem(null);
    setDeletePopup(false);
    dispatch<any>(resetMetaStatuses());
  }, [deleteStreamSuccess, userById]);

  return (
    <div>
      {connectStage === 0 && (
        <div>
          {metaStreams?.length === 0 && streamType === "ALL" ? (
            <div className={styles.noStreamsWrap}>
              <div className={styles.welcomeHead}>Welcome to Meta</div>
              <div className={styles.welcomePara}>
                Your seamless connectivity between our platform and Meta’s suite
                of services, including Facebook and Instagram.
              </div>
              <Button
                variant="contained"
                color="primary"
                sx={{
                  fontSize: 14,
                  textTransform: "capitalize",
                  boxShadow: "none",
                  height: "38px",
                }}
                size={"small"}
                onClick={createStream}
              >
                <AddIcon sx={{ marginRight: "10px", fontSize: "18px" }} />
                Create new stream
              </Button>
            </div>
          ) : (
            <div className={styles.streamsTableWrap}>
              <div className={styles.streamsTabHeader}>
                <div>
                  <div className={styles.creditTitle}>Stream List</div>
                  <div className={styles.creditDesc}>
                    Create and manage your lead stream.
                  </div>
                </div>
                <div className={styles.streamSearchWrap}>
                  <TextField
                    placeholder="Search"
                    value={searchTerm}
                    onChange={handleSearch}
                    inputProps={{
                      style: {
                        background: "#ffffff",
                        padding: "8px 12px",
                      },
                    }}
                    type="search"
                    sx={{ width: "350px" }}
                  />
                </div>
              </div>

              {streamsLoading ? (
                <TableSkeleton />
              ) : (
                <>
                  <div
                    style={{
                      borderRadius: "6PX",
                      border: "2px solid #EAECF0",
                      overflow: "hidden",
                    }}
                  >
                    <TableContainer
                      sx={{
                        borderRadius: "6PX",
                        maxHeight: "calc(100vh - 268px)",
                        minHeight: "calc(100vh - 268px)",
                        overflowY: "auto",
                        background: "#fff",
                      }}
                    >
                      <Table
                        sx={{
                          minWidth: 700,
                        }}
                      >
                        <TableHead
                          sx={{
                            position: "sticky",
                            top: 0,
                            zIndex: 999,
                            background: "#073E53",
                          }}
                        >
                          <TableRow>
                            <StyledTableCellHead
                              sx={{ minWidth: 300, padding: "16px" }}
                            >
                              Stream Name
                            </StyledTableCellHead>
                            <StyledTableCellHead sx={{ minWidth: 150 }}>
                              Stream ID
                            </StyledTableCellHead>
                            <StyledTableCellHead sx={{ minWidth: 200 }}>
                              Ad Account
                            </StyledTableCellHead>
                            <StyledTableCellHead sx={{ minWidth: 180 }}>
                              Status
                            </StyledTableCellHead>

                            <StyledTableCellHead sx={{ minWidth: 200 }}>
                              Total Leads
                            </StyledTableCellHead>
                            <StyledTableCellHead sx={{ minWidth: 200 }}>
                              Last synced
                            </StyledTableCellHead>
                            <StyledTableCellHead sx={{ minWidth: 200 }}>
                              Branch
                            </StyledTableCellHead>
                            <StyledTableCellHead sx={{ minWidth: 240 }}>
                              Actions
                            </StyledTableCellHead>
                          </TableRow>
                        </TableHead>
                        <TableBody
                          sx={{
                            background: "#fff",
                          }}
                        >
                          {filteredMetaStreams?.map((item: any, index: any) => (
                            <TableRow key={index}>
                              <StyledTableCellBody
                                sx={{
                                  textTransform: "capitalize",
                                  fontWeight: "700",
                                }}
                              >
                                {item?.streamName}
                              </StyledTableCellBody>
                              <StyledTableCellBody
                                sx={{
                                  textTransform: "capitalize",
                                  fontWeight: "500",
                                }}
                              >
                                {item?.metaAdId}
                              </StyledTableCellBody>
                              <StyledTableCellBody
                                sx={{ textTransform: "capitalize" }}
                              >
                                {item?.metaAdAccountName}
                              </StyledTableCellBody>
                              <StyledTableCellBody>
                                <div
                                  style={{
                                    background:
                                      item?.status === "ACTIVE"
                                        ? "#11C89B"
                                        : item?.status === "INACTIVE"
                                        ? "#8A94A6"
                                        : "#f1a000",
                                    color: "#FFF",
                                    fontSize: "13PX",
                                    padding: "4px 20px",
                                    width: "fit-content",
                                    borderRadius: "6px",
                                    textTransform: "capitalize",
                                  }}
                                >
                                  {item?.status?.toLowerCase()}
                                </div>
                              </StyledTableCellBody>
                              <StyledTableCellBody>
                                {item?.totalLeadSynced}
                              </StyledTableCellBody>
                              <StyledTableCellBody sx={{ color: "#A1A1AA" }}>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  {item?.lastLeadSyncTime
                                    ? getTimeAgo(item.lastLeadSyncTime)
                                    : "Not synced yet"}
                                  {/* <button className={styles.streamRefreshBtn}>
                            <img src={reloadIcon} alt="refresh" />
                          </button> */}
                                </div>
                              </StyledTableCellBody>
                              <StyledTableCellBody
                                sx={{ textTransform: "capitalize" }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    gap: "4px",
                                    alignItems: "center",
                                    color: "#A1A1AA",
                                  }}
                                >
                                  {
                                    findBranchById(
                                      item?.branch?.[0],
                                      branchList
                                    )?.branchName
                                  }
                                  {item?.branch?.length > 1 && (
                                    <>
                                      <span
                                        style={{
                                          color: "#073E53",
                                          fontSize: "10px",
                                        }}
                                      >
                                        +{item?.branch?.length - 1}
                                      </span>
                                      <Tooltip
                                        arrow
                                        title={
                                          <>
                                            {item?.branch
                                              ?.slice(1)
                                              .map(
                                                (
                                                  branchId: any,
                                                  index: number,
                                                  array: any[]
                                                ) => {
                                                  const branchName =
                                                    findBranchById(
                                                      branchId,
                                                      branchList
                                                    )?.branchName;
                                                  return (
                                                    <span
                                                      key={index}
                                                      style={{
                                                        textTransform:
                                                          "capitalize",
                                                      }}
                                                    >
                                                      {branchName}
                                                      {index <
                                                        array.length - 1 &&
                                                        ", "}
                                                    </span>
                                                  );
                                                }
                                              )}
                                          </>
                                        }
                                        PopperProps={{
                                          style: { zIndex: 9999999 },
                                        }}
                                        componentsProps={{
                                          tooltip: {
                                            sx: {
                                              bgcolor: "#101828",
                                              color: "#fff",
                                              fontSize: "12px",
                                              fontWeight: "500",
                                              padding: "8px 12px",
                                              "& .MuiTooltip-arrow": {
                                                color: "#101828",
                                              },
                                            },
                                          },
                                        }}
                                      >
                                        <span
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            marginLeft: "3PX",
                                          }}
                                        >
                                          <img src={helpIcon} alt="helpIcon" />
                                        </span>
                                      </Tooltip>
                                    </>
                                  )}
                                </div>
                              </StyledTableCellBody>
                              <StyledTableCellBody>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "6px",
                                  }}
                                >
                                  {item?.status !== "DRAFT" && (
                                    <button
                                      className={styles.streamActionBtn}
                                      onClick={() => ViewLeads(item)}
                                    >
                                      View Leads
                                    </button>
                                  )}

                                  <button
                                    className={styles.streamActionBtn}
                                    onClick={() => streamEdit(item)}
                                  >
                                    Edit
                                  </button>
                                  {item?.status !== "ACTIVE" && (
                                    <button
                                      className={styles.streamActionBtn}
                                      onClick={() => handleDeleteStream(item)}
                                    >
                                      Delete
                                    </button>
                                  )}
                                </div>
                              </StyledTableCellBody>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      {filteredMetaStreams?.length === 0 && (
                        <div
                          style={{
                            fontSize: "14px",
                            fontWeight: "500",
                            textTransform: "capitalize",
                            color: " #667085",
                            display: "flex",
                            alignItems: "center",
                            gap: "2px",
                            justifyContent: "center",
                            marginTop: "18%",
                          }}
                        >
                          <div style={{ color: " #142C44" }}>
                            No results found.
                          </div>
                        </div>
                      )}
                    </TableContainer>
                  </div>
                </>
              )}
              <DeletePopup
                open={deletePopup}
                onClose={() => {
                  setDeletePopup(false);
                  setDeleteItem(null);
                }}
                yesBtnClick={handleDelete}
                noBtnClick={() => {
                  setDeletePopup(false);
                  setDeleteItem(null);
                }}
                loader={deleteStreamLoading}
                title={"Are you sure you want to delete the stream?"}
                description={
                  deleteItem?.status === "INACTIVE"
                    ? "Deleting the stream will result in removal of all the contact data associated with the stream"
                    : ""
                }
              />
            </div>
          )}
        </div>
      )}
      {connectStage === 1 && (
        <ConnectStream
          setConnectStage={setConnectStage}
          validStateName={validStateName}
          streamName={streamName}
          handleStreamName={handleStreamName}
          setValidStateName={setValidStateName}
          setStreamName={setStreamName}
        />
      )}
      {connectStage === 2 && (
        <ConnectFields
          setConnectStage={setConnectStage}
          validStateName={validStateName}
          streamName={streamName}
          handleStreamName={handleStreamName}
        />
      )}
    </div>
  );
}

export default StreamsTab;
